import React, { useEffect, useState } from "react";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import CircularProgress from "@material-ui/core/CircularProgress";
import Alert from "@material-ui/lab/Alert";
import { makeStyles } from "@material-ui/core/styles";
import { FaMicrophone, FaStop } from "react-icons/fa";
import { RiStarLine } from "react-icons/ri";
import useBDocTranslator from "../../hooks/useBDocTranslator";
import { MAX_DICTATION_MS } from "../../hooks/useSpeechToText";

export const AI_COLOR = "#7B2CBF";
const LISTEN_COLOR = "#d92d20";

const useStyles = makeStyles((theme) => ({
  "@keyframes dictationPulse": {
    "0%": { boxShadow: `0 0 0 0 rgba(217, 45, 32, 0.45)` },
    "70%": { boxShadow: `0 0 0 8px rgba(217, 45, 32, 0)` },
    "100%": { boxShadow: `0 0 0 0 rgba(217, 45, 32, 0)` },
  },
  // "&&&&" sube la especificidad por encima de ".ql-snow.ql-toolbar button",
  // que fuerza ancho de 28px a todo botón dentro de la barra de Quill.
  startButton: {
    "&&&&": {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      width: "auto",
      height: "auto",
      float: "none",
      border: `1px solid rgba(123, 44, 191, 0.35)`,
      borderRadius: 999,
      background: "rgba(123, 44, 191, 0.06)",
      color: AI_COLOR,
      fontFamily: theme.typography.fontFamily,
      fontSize: 12,
      fontWeight: 600,
      lineHeight: "18px",
      whiteSpace: "nowrap",
      padding: "3px 10px",
      cursor: "pointer",
    },
    "&&&& svg": {
      float: "none",
      width: "auto",
      height: "auto",
    },
    "&&&&:hover:not(:disabled)": {
      background: "rgba(123, 44, 191, 0.12)",
      color: AI_COLOR,
    },
    "&&&&:disabled": {
      opacity: 0.5,
      cursor: "default",
    },
    "&&&&:focus-visible": {
      outline: `2px solid ${AI_COLOR}`,
      outlineOffset: 2,
    },
  },
  panel: {
    border: "1px solid",
    borderRadius: 8,
    padding: theme.spacing(1.5, 2),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  listening: {
    borderColor: "rgba(217, 45, 32, 0.35)",
    background: "rgba(217, 45, 32, 0.04)",
  },
  refining: {
    borderColor: "rgba(123, 44, 191, 0.3)",
    background: "rgba(123, 44, 191, 0.04)",
  },
  review: {
    borderColor: "rgba(123, 44, 191, 0.3)",
    background: "#fff",
  },
  panelHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    fontSize: 13,
    fontWeight: 600,
  },
  dot: {
    width: 10,
    height: 10,
    borderRadius: "50%",
    background: LISTEN_COLOR,
    animation: "$dictationPulse 1.4s infinite",
    flexShrink: 0,
  },
  timer: {
    fontVariantNumeric: "tabular-nums",
    fontWeight: 400,
    color: theme.palette.text.secondary,
  },
  spacer: {
    flex: 1,
  },
  live: {
    fontSize: 14,
    lineHeight: 1.5,
    color: theme.palette.text.primary,
    minHeight: 21,
    maxHeight: 120,
    overflowY: "auto",
  },
  interim: {
    color: theme.palette.text.secondary,
  },
  placeholder: {
    color: theme.palette.text.secondary,
    fontStyle: "italic",
  },
  original: {
    fontSize: 13,
    color: theme.palette.text.secondary,
    background: theme.palette.grey[50],
    borderRadius: 6,
    padding: theme.spacing(1, 1.5),
    whiteSpace: "pre-wrap",
  },
  actions: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
  },
  linkButton: {
    marginRight: "auto",
    textTransform: "none",
  },
  hint: {
    fontSize: 12,
    color: theme.palette.text.secondary,
  },
}));

const formatElapsed = (ms) => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = String(total % 60).padStart(2, "0");
  return `${minutes}:${seconds}`;
};

const SPEECH_ERROR_MESSAGES = {
  "microphone-denied":
    "Microphone access is blocked. Allow it in the browser address bar and try again.",
  "no-microphone": "No microphone was found on this device.",
  network: "Speech recognition needs an internet connection. Try again.",
  "language-not-supported": "This browser does not support dictation in this language.",
  "no-speech": "No speech was detected. Try again closer to the microphone.",
};

/**
 * Botón para empezar a dictar. En navegadores sin soporte muestra un aviso.
 */
export function DictationStartButton({ dictation, disabled }) {
  const classes = useStyles();
  const { t } = useBDocTranslator();

  if (!dictation.isSupported) {
    return (
      <span className={classes.hint}>
        {t("Voice dictation is available in Chrome and Edge")}
      </span>
    );
  }

  return (
    <button
      type="button"
      className={classes.startButton}
      onClick={dictation.start}
      disabled={disabled || dictation.phase !== "idle"}
      aria-label={t("Start voice dictation")}
    >
      <FaMicrophone size={12} />
      {t("Dictate with AI")}
      <RiStarLine size={10} />
    </button>
  );
}

/**
 * Estado del dictado: escuchando, procesando con IA, revisión y errores.
 * No muestra nada cuando no hay un dictado en curso ni errores.
 */
export default function DictationPanel({ dictation }) {
  const classes = useStyles();
  const { t } = useBDocTranslator();
  const [now, setNow] = useState(Date.now());
  const [showOriginal, setShowOriginal] = useState(false);

  const { phase } = dictation;

  useEffect(() => {
    if (phase !== "listening") return undefined;
    const timer = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(timer);
  }, [phase]);

  useEffect(() => {
    if (phase !== "review") setShowOriginal(false);
  }, [phase]);

  const speechErrorMessage =
    dictation.speechError &&
    t(
      SPEECH_ERROR_MESSAGES[dictation.speechError] ||
        "Voice dictation failed. Try again."
    );

  if (phase === "listening") {
    const elapsed = dictation.startedAt ? now - dictation.startedAt : 0;
    const hasText = dictation.transcript || dictation.interimText;
    return (
      <div
        className={`${classes.panel} ${classes.listening}`}
        role="status"
        aria-live="polite"
      >
        <div className={classes.panelHeader}>
          <span className={classes.dot} />
          <span style={{ color: LISTEN_COLOR }}>{t("Listening...")}</span>
          <span className={classes.timer}>
            {formatElapsed(elapsed)} / {formatElapsed(MAX_DICTATION_MS)}
          </span>
          <span className={classes.spacer} />
          <Button size="small" onClick={dictation.cancel}>
            {t("Cancel")}
          </Button>
          <Button
            size="small"
            variant="contained"
            disableElevation
            startIcon={<FaStop size={10} />}
            onClick={dictation.stop}
            style={{ background: LISTEN_COLOR, color: "#fff" }}
          >
            {t("Stop")}
          </Button>
        </div>
        <div className={classes.live}>
          {hasText ? (
            <>
              {dictation.transcript}{" "}
              <span className={classes.interim}>{dictation.interimText}</span>
            </>
          ) : (
            <span className={classes.placeholder}>
              {t("Speak normally. Press Stop when you finish.")}
            </span>
          )}
        </div>
      </div>
    );
  }

  if (phase === "refining") {
    return (
      <div
        className={`${classes.panel} ${classes.refining}`}
        role="status"
        aria-live="polite"
      >
        <div className={classes.panelHeader} style={{ color: AI_COLOR }}>
          <CircularProgress size={14} style={{ color: AI_COLOR }} />
          {t("Processing with AI...")}
        </div>
        <div className={classes.live}>
          <span className={classes.interim}>{dictation.rawText}</span>
        </div>
      </div>
    );
  }

  if (phase === "review") {
    const changed =
      dictation.refinedText.trim() !== dictation.rawText.trim() &&
      !dictation.refineError;
    return (
      <div className={`${classes.panel} ${classes.review}`}>
        <div className={classes.panelHeader} style={{ color: AI_COLOR }}>
          <RiStarLine size={14} />
          {t("Review the text before inserting it")}
        </div>
        {dictation.refineError && (
          <Alert severity="warning">
            {dictation.refineError === "ai-disabled"
              ? t("AI features are currently disabled")
              : t("The text could not be processed with AI.")}{" "}
            {t("The original dictated text is shown.")}
          </Alert>
        )}
        <TextField
          multiline
          fullWidth
          variant="outlined"
          minRows={2}
          maxRows={8}
          value={dictation.refinedText}
          onChange={(e) => dictation.setRefinedText(e.target.value)}
          helperText={t(
            "Check medications and doses. You can edit the text here."
          )}
          inputProps={{ "aria-label": t("Dictated text") }}
        />
        {showOriginal && (
          <div className={classes.original}>
            <strong>{t("Original dictation")}:</strong> {dictation.rawText}
          </div>
        )}
        <div className={classes.actions}>
          {changed && (
            <Button
              size="small"
              color="primary"
              className={classes.linkButton}
              onClick={() => setShowOriginal((value) => !value)}
            >
              {showOriginal ? t("Hide original") : t("Show original")}
            </Button>
          )}
          <Button size="small" onClick={dictation.discard}>
            {t("Discard")}
          </Button>
          {changed && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => dictation.insert(dictation.rawText)}
            >
              {t("Use original")}
            </Button>
          )}
          <Button
            size="small"
            variant="contained"
            disableElevation
            onClick={() => dictation.insert(dictation.refinedText)}
            style={{ background: AI_COLOR, color: "#fff" }}
            disabled={!dictation.refinedText.trim()}
          >
            {t("Insert")}
          </Button>
        </div>
      </div>
    );
  }

  if (speechErrorMessage) {
    return (
      <Alert severity="warning" onClose={dictation.clearSpeechError}>
        {speechErrorMessage}
      </Alert>
    );
  }

  return null;
}
